import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Button } from '../components/ui/Button';
import { Switch } from '../components/ui/Switch';
import { Alert } from '../components/ui/Alert';
import { Zap } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import { Sheet, SheetContent } from '../components/ui/Sheet';
import { BackInStockSection } from '../components/BackInStockSection';
import { AutomationEditPane } from '../components/AutomationEditPane';
import { BuiltinPreviewPane } from '../components/BuiltinPreviewPane';
import { apiFetch } from '../lib/api';
import {
  createDesignMails,
  missingMailsNote,
  withDesignMails,
  type DesignMailRef,
} from '../lib/designMails';
import { formatMoney, formatNumber } from '../lib/format';
import { useAuth } from '../lib/auth';
import { TRIGGER_LABELS } from '../lib/automationLabels';
import { ApiError } from '../types/api';
import type {
  AutomationDetailResponse,
  AutomationListItem,
  AutomationsListResponse,
  BuiltInFlowKey,
} from '../types/api';

interface BuiltinSummary {
  key: BuiltInFlowKey;
  slug: string;
  name: string;
  triggerType: string;
}

function AutomationRow({
  automation,
  onEdit,
}: {
  automation: AutomationListItem;
  onEdit: () => void;
}) {
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  const enableMutation = useMutation({
    mutationFn: () => apiFetch(`/automations/${automation.id}/enable`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['automation', automation.id] });
      void queryClient.invalidateQueries({ queryKey: ['automations'] });
    },
  });
  const disableMutation = useMutation({
    mutationFn: () => apiFetch(`/automations/${automation.id}/disable`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['automation', automation.id] });
      void queryClient.invalidateQueries({ queryKey: ['automations'] });
    },
  });

  const toggling = enableMutation.isPending || disableMutation.isPending;
  const toggleError =
    enableMutation.error instanceof ApiError
      ? (enableMutation.error.body.message ?? 'Could not be enabled.')
      : disableMutation.error instanceof ApiError
        ? (disableMutation.error.body.message ?? 'Could not be disabled. Try again.')
        : disableMutation.isError
          ? 'Could not be disabled. Try again.'
          : null;

  return (
    <>
      <tr className="border-t border-border-soft">
        <td className="px-4 py-3">
          <button type="button" onClick={onEdit} className="text-left">
            <span className="font-medium block">{automation.name}</span>
            <span className="text-xs text-muted block mt-0.5">
              {TRIGGER_LABELS[automation.triggerType]}
            </span>
          </button>
        </td>
        <td className="px-4 py-3 text-right font-mono">{formatNumber(automation.stats.active)}</td>
        <td className="px-4 py-3 text-right font-mono">{formatNumber(automation.stats.sent)}</td>
        <td className="px-4 py-3 text-right font-mono">
          {formatMoney(automation.stats.revenue, automation.stats.currency)}
        </td>
        <td className="px-4 py-3">
          <Switch
            checked={automation.enabled}
            disabled={toggling || !isAdmin}
            label={`Turn ${automation.name} ${automation.enabled ? 'off' : 'on'}`}
            onChange={() =>
              automation.enabled ? disableMutation.mutate() : enableMutation.mutate()
            }
          />
        </td>
        <td className="px-4 py-3 text-right">
          <Button variant="secondary" size="sm" onClick={onEdit}>
            Edit
          </Button>
        </td>
      </tr>
      {toggleError ? (
        <tr>
          <td colSpan={6} className="px-4 pb-3">
            <Alert>{toggleError}</Alert>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** Installs a built-in (always switched off) and opens it, from its row or its preview. */
function useInstallBuiltin(key: BuiltInFlowKey, onInstalled?: () => void) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      const installed = await apiFetch<{
        automation: AutomationListItem;
        mails?: DesignMailRef[];
      }>(`/automations/builtins/${key}/install`, { method: 'POST' });
      // NoPain's design mails are created one request each (a Workers Free
      // plan caps the queries per request), then the flow is saved once
      // with all of them. If that fails the flow stays installed without
      // mails; "Use the NoPain design mails" in its edit pane tries again.
      let notice: string | null = null;
      if ((installed.mails ?? []).length > 0) {
        try {
          const id = installed.automation.id;
          const mails = await createDesignMails(id, installed.mails ?? []);
          const detail = await apiFetch<AutomationDetailResponse>(`/automations/${id}`);
          await apiFetch(`/automations/${id}`, {
            method: 'PUT',
            body: { definition: withDesignMails(detail.definition, mails) },
          });
          notice = missingMailsNote(mails.failed);
        } catch {
          // Installed anyway, but say so: the flow has no mails yet.
          notice =
            'The flow was installed, but its design mails could not be created. Use "Use the NoPain design mails" to try again.';
        }
      }
      return { ...installed, notice };
    },
    onSuccess: ({ automation, notice }) => {
      void queryClient.invalidateQueries({ queryKey: ['automations'] });
      onInstalled?.();
      navigate(`/automatiseringer/${automation.id}`, { state: notice ? { notice } : null });
    },
  });
  const error =
    mutation.error instanceof ApiError
      ? (mutation.error.body.message ?? 'Could not be installed. Try again.')
      : mutation.isError
        ? 'Could not be installed. Try again.'
        : null;
  return { install: () => mutation.mutate(), installing: mutation.isPending, error };
}

function InstallRow({ builtin, onPreview }: { builtin: BuiltinSummary; onPreview: () => void }) {
  const { install, installing } = useInstallBuiltin(builtin.key);

  return (
    <div className="flex items-center gap-2 px-4 py-3 border-b border-border-soft last:border-b-0">
      <button type="button" onClick={onPreview} className="flex-grow text-left text-sm text-muted">
        {builtin.name}
      </button>
      <Button variant="ghost" size="sm" onClick={onPreview}>
        Preview
      </Button>
      <Button variant="secondary" size="sm" disabled={installing} onClick={install}>
        {installing ? 'Installing…' : 'Install'}
      </Button>
    </div>
  );
}

function BuiltinPreviewSheet({
  builtin,
  onClose,
}: {
  builtin: BuiltinSummary | null;
  onClose: () => void;
}) {
  return (
    <Sheet
      open={builtin !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {builtin ? <BuiltinPreviewContent builtin={builtin} onClose={onClose} /> : null}
    </Sheet>
  );
}

function BuiltinPreviewContent({
  builtin,
  onClose,
}: {
  builtin: BuiltinSummary;
  onClose: () => void;
}) {
  const { install, installing, error } = useInstallBuiltin(builtin.key, onClose);
  return (
    <SheetContent
      className="w-[640px]"
      eyebrow="Preview · not installed"
      title={builtin.name}
      description={`What the automation ${builtin.name} does, step by step, with its mails.`}
    >
      <BuiltinPreviewPane
        builtinKey={builtin.key}
        onInstall={install}
        installing={installing}
        installError={error}
        onClose={onClose}
      />
    </SheetContent>
  );
}

export function AutomatiseringerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [previewKey, setPreviewKey] = useState<BuiltInFlowKey | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['automations'],
    queryFn: () => apiFetch<AutomationsListResponse>('/automations'),
  });
  const { data: builtinsData } = useQuery({
    queryKey: ['automations', 'builtins'],
    queryFn: () => apiFetch<{ builtins: BuiltinSummary[] }>('/automations/builtins'),
  });
  const { data: selectedDetail } = useQuery({
    queryKey: ['automation', id],
    queryFn: () => apiFetch<AutomationDetailResponse>(`/automations/${id}`),
    enabled: Boolean(id),
  });

  const automations = data?.automations ?? [];
  const installedSlugs = new Set(automations.map((a) => a.slug));
  const installable = (builtinsData?.builtins ?? []).filter((b) => !installedSlugs.has(b.slug));
  const previewing = installable.find((b) => b.key === previewKey) ?? null;
  const selectedName =
    selectedDetail?.automation.name ?? automations.find((a) => a.id === id)?.name ?? 'Automation';

  // A note from installing a built-in whose design mails were not all created.
  // It is taken out of the history entry at once, so a reload or going back
  // later never shows it again after the mails have been fixed.
  const [installNotice, setInstallNotice] = useState<{ id: string; text: string } | null>(null);
  useEffect(() => {
    const text = (location.state as { notice?: string } | null)?.notice;
    if (!text || !id) return;
    setInstallNotice({ id, text });
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, id, navigate]);

  function closeSheet() {
    navigate('/automatiseringer');
  }

  return (
    <>
      <PageHeader
        title="Automations"
        badge={<MockBadge />}
        description="Built-in flows that send automatically, based on contacts' behavior."
      />

      {isLoading ? (
        <div className="text-muted">Loading automations…</div>
      ) : (
        <>
          {automations.length === 0 ? (
            <EmptyState
              icon={<Zap />}
              title="No automations yet"
              description="Automations are built-in flows that send by themselves when a contact does something, such as signing up."
            />
          ) : (
            <div className="border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left text-xs text-muted uppercase tracking-wide">
                    <th className="px-4 py-2.5 font-medium">Automation</th>
                    <th className="px-4 py-2.5 font-medium text-right">Active</th>
                    <th className="px-4 py-2.5 font-medium text-right">Sent</th>
                    <th className="px-4 py-2.5 font-medium text-right">Revenue</th>
                    <th className="px-4 py-2.5 font-medium">On/off</th>
                    <th className="px-4 py-2.5 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {automations.map((a) => (
                    <AutomationRow
                      key={a.id}
                      automation={a}
                      onEdit={() => navigate(`/automatiseringer/${a.id}`)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {installable.length > 0 ? (
            <div className="border border-border rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 text-xs text-muted uppercase tracking-wide border-b border-border-soft">
                Not installed
              </div>
              {installable.map((b) => (
                <InstallRow key={b.key} builtin={b} onPreview={() => setPreviewKey(b.key)} />
              ))}
            </div>
          ) : null}
        </>
      )}

      <BackInStockSection />

      <BuiltinPreviewSheet builtin={previewing} onClose={() => setPreviewKey(null)} />

      <Sheet
        open={Boolean(id)}
        onOpenChange={(open) => {
          if (!open) closeSheet();
        }}
      >
        {id ? (
          <SheetContent
            eyebrow="Edit flow"
            title={selectedName}
            description={`Edit the settings, steps and timeline for the automation ${selectedName}.`}
          >
            <AutomationEditPane
              id={id}
              onClose={closeSheet}
              notice={installNotice?.id === id ? installNotice.text : null}
            />
          </SheetContent>
        ) : null}
      </Sheet>
    </>
  );
}
